import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './CollapsibleWrapper.css';

/**
 * 🔧 模块级别状态缓存 - 解决虚拟列表卸载/重新挂载时状态丢失问题
 * 使用 Map 存储每个消息的展开状态，key 为 messageId
 */
const expandedStateCache = new Map();

/**
 * 🔧 常量默认值 - 避免每次渲染创建新对象/函数
 */
const NOOP = () => {};

/**
 * 优化版折叠包装器组件
 * 1. 使用memo减少不必要的重新渲染
 * 2. 优化行高计算逻辑，减少DOM操作
 * 3. 降低检测频率，提高性能
 * 4. 支持初始展开状态和状态变化通知
 * 5. 🔧 新增：使用模块级缓存保持折叠状态跨越虚拟列表的卸载/挂载周期
 */
const CollapsibleWrapper = memo(({ 
  children, 
  messageId = null,  // 🔧 新增：消息唯一标识，用于状态缓存
  collapsible = false, // 🔧 固定高度策略：由上层决定是否折叠
  initialExpanded = false, 
  onToggle = NOOP  // 🔧 使用常量默认值
}) => {
  const { t } = useTranslation();
  // 🔧 从缓存读取状态，如果没有则使用 initialExpanded
  const [isExpanded, setIsExpanded] = useState(() => {
    if (messageId && expandedStateCache.has(messageId)) {
      return expandedStateCache.get(messageId);
    }
    return initialExpanded;
  });
  const contentRef = useRef(null);
  const initialRenderRef = useRef(true);
  const needsCollapsing = collapsible;
  
  // 🔧 状态变化时更新缓存 + 通知父组件
  useEffect(() => {
    if (messageId) {
      expandedStateCache.set(messageId, isExpanded);
    }
    // 跳过初始渲染
    if (!initialRenderRef.current && onToggle !== NOOP) {
      onToggle(isExpanded);
    }
  }, [messageId, isExpanded, onToggle]);
  
  // 🔧 固定高度策略：不再测量内容高度，避免任何动态高度变化
  
  // 处理初始展开状态
  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      if (initialExpanded) {
        setIsExpanded(true);
      }
    }
  }, [initialExpanded]);
  
  // 🔧 使用 useCallback 稳定函数引用，使用函数式更新避免闭包问题
  const toggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);
  
  return (
    <div className="collapsible-wrapper">
      <div
        ref={contentRef}
        className={`collapsible-content ${(needsCollapsing && !isExpanded) ? 'collapsed' : ''}`}
        data-expanded={isExpanded}
      >
        {children}
      </div>
      
      {needsCollapsing && (
        <button 
          className="collapsible-toggle-button"
          onClick={toggleExpand}
        >
          {isExpanded ? t('collapsible_collapse') : t('collapsible_expand')}
        </button>
      )}
    </div>
  );
});

export default CollapsibleWrapper; 